import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Landmark, ShieldCheck, MessageSquareWarning, CalendarX, ChevronRight, GraduationCap, UsersRound, ScrollText, Compass, Lightbulb } from 'lucide-react';
import { STATUTS_CP } from '../conseil/constantes';

export const Ligne = ({ to, Icone, label, valeur, alerte, testId }) => (
  <Link to={to} data-testid={testId}
    className={`group/ligne flex items-center gap-3 rounded-xl px-3 py-2 transition-colors duration-200 ${alerte ? 'bg-red-500/90 hover:bg-red-500' : 'bg-white/10 hover:bg-white/20'}`}>
    <Icone className="h-4 w-4 shrink-0 opacity-90" />
    <span className="flex-1 text-sm font-medium">{label}</span>
    <span className="whitespace-nowrap text-sm font-bold" data-testid={`${testId}-valeur`}>{valeur}</span>
    <ChevronRight className="h-4 w-4 opacity-60 transition-transform duration-200 group-hover/ligne:translate-x-0.5" />
  </Link>
);

// Administratif CFA : Qualiopi, réclamations et demandes d'absence ; alerte dès qu'une demande arrive
export const TuileAdministratif = ({ absences }) => {
  const [q, setQ] = useState(null);
  const [form, setForm] = useState(null);
  const [conseil, setConseil] = useState(undefined);
  const [reglement, setReglement] = useState(null);
  const [guide, setGuide] = useState(null);
  const annee = new Date().getFullYear();
  useEffect(() => {
    axios.get('/qualiopi').then(({ data }) => setQ({ score: data.score, reclamations: data.compteurs?.reclamations_nouvelles || 0, actions: data.compteurs?.actions_ouvertes || 0 })).catch(() => setQ({ score: null, reclamations: 0, actions: 0 }));
    axios.get('/charte-formateurs/suivi').then(({ data }) => setForm(data.compteurs)).catch(() => setForm(null));
    axios.get('/guide-bpjeps/config').then(({ data }) => setGuide(data)).catch(() => setGuide(null));
    axios.get('/reglement-interieur/suivi').then(({ data }) => setReglement(data.compteurs)).catch(() => setReglement(null));
    axios.get('/conseil-perfectionnement').then(({ data }) => setConseil(data.find((c) => c.annee === annee) || null)).catch(() => setConseil(null));
  }, [annee]);
  const reclamations = q?.reclamations || 0;
  const total = reclamations + (absences || 0);
  return (
    <div className={`rounded-2xl bg-[#334155] p-5 sm:p-6 text-white shadow-sm ${total > 0 ? 'ring-4 ring-red-500 ring-offset-2' : ''}`} data-testid="tuile-administratif">
      <div className="flex items-start justify-between gap-3">
        <div className="rounded-xl bg-white/15 p-2.5"><Landmark className="h-6 w-6" /></div>
        {total > 0 && (
          <span className="flex h-7 min-w-[28px] items-center justify-center rounded-full bg-red-500 px-2 text-sm font-bold shadow-lg animate-pulse" data-testid="tuile-administratif-pastille">
            {total} nouveau{total > 1 ? 'x' : ''}
          </span>
        )}
      </div>
      <p className="mt-4 text-sm font-medium text-white/80">Administratif CFA</p>
      <div className="mt-2 space-y-2">
        <Ligne to="/qualiopi" Icone={ShieldCheck} label="Qualiopi" testId="tuile-administratif-qualiopi"
          valeur={q?.score ? `${q.score.pourcentage} % · ${q.score.vert}/${q.score.applicables}` : '…'} />
        <Ligne to="/qualiopi?onglet=reclamations" Icone={MessageSquareWarning} label="Réclamations" testId="tuile-administratif-reclamations"
          valeur={q ? `${reclamations} nouvelle${reclamations > 1 ? 's' : ''}` : '…'} alerte={reclamations > 0} />
        <Ligne to="/demandes-absence" Icone={CalendarX} label="Demandes d'absence" testId="tuile-administratif-absences"
          valeur={`${absences || 0} en attente`} alerte={absences > 0} />
        <Ligne to="/administratif/reglement-interieur" Icone={ScrollText} label="Règlement intérieur" testId="tuile-administratif-reglement"
          valeur={reglement ? `${reglement.signes}/${reglement.stagiaires} signés` : '…'} />
        {guide && (
          <Ligne to="/guide-bc1-bc2?onglet=pilotage" Icone={Compass} label="Guide BC1 / BC2" testId="tuile-administratif-guide"
            valeur={guide.ouvert_stagiaires ? 'Ouvert' : 'Fermé'} />
        )}
        <Ligne to="/administratif/formateurs" Icone={GraduationCap} label="Formateurs" testId="tuile-administratif-formateurs"
          valeur={form ? `${form.chartes_signees}/${form.formateurs} chartes` : '…'} />
        <Ligne to="/administratif/conseil-perfectionnement" Icone={UsersRound} label="Conseil de perfectionnement" testId="tuile-administratif-conseil"
          valeur={conseil === undefined ? '…' : `${annee} · ${conseil ? STATUTS_CP[conseil.statut] : 'à créer'}`} />
        <Ligne to="/administratif/amelioration-continue" Icone={Lightbulb} label="Amélioration continue" testId="tuile-administratif-amelioration"
          valeur={q ? `${q.actions} action${q.actions > 1 ? 's' : ''} ouverte${q.actions > 1 ? 's' : ''}` : '…'} />
      </div>
    </div>
  );
};
